import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supportAPI } from '@/services/api';
import { authAPI } from '@/services/authAPI';
import { Lock, User, LifeBuoy, ArrowLeft, Loader2, KeyRound, Phone } from 'lucide-react';
import PasswordInput from '@/components/shared/PasswordInput';
import ThemeToggle from '@/components/shared/ThemeToggle';

const SUPPORT_DOMAIN = 'numuv.app';

export default function SupportSignup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', localpart: '', password: '', phone: '', invite_code: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [legalVersions, setLegalVersions] = useState({ terms_version: '', privacy_version: '' });

  useEffect(() => {
    authAPI.getLegalVersions().then(setLegalVersions).catch(() => { /* ignore */ });
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    try {
      const localpart = form.localpart.trim().toLowerCase().split('@')[0];
      if (!localpart) {
        setError('Username is required'); setLoading(false); return;
      }
      const email = `${localpart}@${SUPPORT_DOMAIN}`;
      const r = await supportAPI.agentSignup({
        name: form.name.trim(),
        email,
        password: form.password,
        phone: form.phone.trim(),
        invite_code: form.invite_code.trim(),
        consent_terms_version: legalVersions.terms_version || null,
        consent_privacy_version: legalVersions.privacy_version || null,
      });
      setSuccessMsg(r.message || 'Application submitted. Admin will review and activate your account.');
      // Do NOT auto-login — agent must wait for admin approval before signing in.
      setTimeout(() => navigate('/login', { replace: true }), 3500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0a0b] flex items-center justify-center px-6 py-10" data-testid="support-signup-page">
      <ThemeToggle className="fixed top-4 right-4 z-50" />
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-slate-500 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 text-sm mb-8" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
          <span className="text-slate-900 dark:text-white">Apply to be a </span>
          <span className="text-emerald-600 dark:text-emerald-400">support agent</span>
        </h1>
        <p className="text-slate-500 dark:text-zinc-500 mb-6 text-sm flex items-center gap-1.5">
          <LifeBuoy className="w-4 h-4" /> Numuv admin will review your application.
        </p>

        {successMsg && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 px-4 py-3 rounded-lg text-sm mb-4" data-testid="support-signup-success">
            {successMsg}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 px-4 py-2.5 rounded-lg text-sm" data-testid="support-signup-error">{error}</div>}

          <Field icon={User} label="Full name" value={form.name} onChange={v => setForm({ ...form, name: v })} testId="agent-name" required />

          {/* Locked-domain email field */}
          <div>
            <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1.5">Email</label>
            <div className="flex items-stretch bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg focus-within:border-slate-300 dark:focus-within:border-zinc-600 overflow-hidden">
              <input
                type="text"
                value={form.localpart}
                onChange={e => setForm({ ...form, localpart: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') })}
                required
                placeholder="yourname"
                data-testid="agent-email-localpart"
                className="flex-1 bg-transparent px-3 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none text-sm"
              />
              <div className="flex items-center px-3 text-slate-500 dark:text-zinc-500 text-sm border-l border-slate-200 dark:border-zinc-800 bg-slate-50 dark:bg-[#0a0a0b] select-none" data-testid="agent-email-domain">
                @{SUPPORT_DOMAIN}
              </div>
            </div>
            <p className="text-slate-500 dark:text-zinc-600 text-xs mt-1">Only the part before @ is editable. The domain is fixed.</p>
          </div>

          <div>
            <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1.5">Password</label>
            <PasswordInput
              value={form.password}
              onChange={v => setForm({ ...form, password: v })}
              onChangeValue
              testId="agent-password"
              placeholder="Min 6 characters"
              required
              minLength={6}
              autoComplete="new-password"
              icon={Lock}
              className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-11 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 text-sm"
            />
          </div>
          <Field icon={Phone} label="Phone (optional)" value={form.phone} onChange={v => setForm({ ...form, phone: v })} testId="agent-phone" />

          <Field
            icon={KeyRound}
            label="Invite code (provided by admin)"
            value={form.invite_code}
            onChange={v => setForm({ ...form, invite_code: v.toUpperCase().replace(/\s/g, '') })}
            testId="agent-invite-code"
            required
          />

          <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="support-tos-accept-row">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-0.5 accent-emerald-500 h-4 w-4 flex-shrink-0"
              data-testid="support-tos-accept-checkbox"
            />
            <span className="text-xs text-slate-500 dark:text-zinc-400 leading-relaxed">
              I have read and agree to Numuv&apos;s{' '}
              <Link to="/terms" target="_blank" rel="noopener noreferrer" data-testid="support-tos-terms-link" className="text-emerald-600 dark:text-emerald-400 hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer" data-testid="support-tos-privacy-link" className="text-emerald-600 dark:text-emerald-400 hover:underline">Privacy Policy</Link>,
              and understand my activity as an internal agent will be logged and audited.
            </span>
          </label>

          <button
            type="submit"
            disabled={loading || !acceptedTerms}
            className="w-full bg-slate-900 dark:bg-white text-white dark:text-black font-medium py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="agent-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, type = 'text', testId, required }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 dark:text-zinc-400 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-500" />
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          required={required}
          data-testid={testId}
          className="w-full bg-white dark:bg-[#111114] border border-slate-200 dark:border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-600 focus:outline-none focus:border-slate-300 dark:focus:border-zinc-600 text-sm"
        />
      </div>
    </div>
  );
}
